import api from './api';

export const inviteService = {
  // ============================================
  // Owner Endpoints - Manage Invitations
  // ============================================

  /**
   * Create a new employee invitation
   * Returns invite with shareable link
   */
  createInvite: async (inviteData) => {
    const response = await api.post('/invite', inviteData);
    return response.data;
  },

  /**
   * Get all invitations for the business
   * Optional status filter: 'Pending', 'Accepted', 'Expired', 'Cancelled'
   */
  getAllInvites: async (status = null) => {
    const url = status ? `/invite?status=${status}` : '/invite';
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get a specific invitation by ID
   */
  getInviteById: async (inviteId) => {
    const response = await api.get(`/invite/${inviteId}`);
    return response.data;
  },

  /**
   * Cancel/revoke a pending invitation
   */
  cancelInvite: async (inviteId) => {
    const response = await api.post(`/invite/${inviteId}/cancel`);
    return response.data;
  },

  /**
   * Resend an invitation (generates new token with new expiration)
   */
  resendInvite: async (inviteId, message = null) => {
    const response = await api.post(`/invite/${inviteId}/resend`, { 
      inviteId,
      message 
    });
    return response.data;
  },

  // ============================================
  // Public Endpoints - Employee Registration
  // ============================================

  /**
   * Validate an invite token (public - no auth required)
   * Used by frontend to check if token is valid before showing registration
   */
  validateInviteToken: async (token) => {
    const response = await api.get(`/invite/validate/${token}`);
    return response.data;
  },

  /**
   * Register as an employee using an invitation token
   */
  registerEmployee: async (registrationData) => {
    const response = await api.post('/auth/register-employee', registrationData);
    return response.data;
  },

  // ============================================
  // Helper Methods
  // ============================================

  /**
   * Copy invite link to clipboard
   */
  copyInviteLink: (inviteLink) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(inviteLink);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        textArea.remove();
        return Promise.resolve();
      } catch (err) {
        textArea.remove();
        return Promise.reject(err);
      }
    }
  },

  /**
   * Get formatted expiration status
   */
  getExpirationStatus: (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const daysRemaining = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));

    if (daysRemaining < 0) {
      return { status: 'expired', text: 'Expired', color: 'red' };
    } else if (daysRemaining === 0) {
      return { status: 'today', text: 'Expires today', color: 'orange' };
    } else if (daysRemaining === 1) {
      return { status: 'soon', text: '1 day left', color: 'orange' };
    } else {
      return { status: 'active', text: `${daysRemaining} days left`, color: 'green' };
    }
  }
};

export default inviteService;